import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import * as buffer from 'buffer';
import { RestfulEndpoint } from '../../services/restful-endpoint.service';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { Bim360Object } from '../p-dashboard/bim-viewer/bim-viewer.component';
import { ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@ngneat/transloco';
import { Project360Service } from '../p-dashboard/project360.service';

@Injectable({
  providedIn: 'root',
})
export class Bim360Service {
  authorizationCode: string;
  apiBaseUrl = `https://developer.api.autodesk.com`;
  redirectUri = `${environment.base_url}/bim360`;
  //BIM360 API Authentication base url
  authBaseUrl = `${this.apiBaseUrl}/authentication/v2`;
  hubsEndpointUrl = `${this.apiBaseUrl}/project/v1/hubs`;
  ProjectsEndpointUrl = `${this.apiBaseUrl}/data/v1/projects`;
  projects: any;
  fileStorageId: string;
  bimfileName: string;
  bimFileLastmodifiedTime: string;
  bimFileId: string;
  bimProjectList: Bim360Object[] = [];

  //Authentication BaseCode for token request header
  authBaseCode = this.generateBasicAuthHeader(environment.bim360Id, environment.bim360SecretId);
  private project_name: string;

  //scopes used in bim360 API
  scopes = 'viewables:read data:read data:write bucket:create account:read account:write';

  //get access token endpoint url
  tokenEndpointUrl = `${this.authBaseUrl}/token`;
  private _hubId: string;
  private _projectId: string;

  //sign in BIM360 end point url
  authUrl = `${this.authBaseUrl}/authorize?response_type=code&client_id=${
    environment.bim360Id
  }&redirect_uri=${encodeURIComponent(this.redirectUri)}&scope=${this.scopes}`;

  isLoggedIn: boolean = false;
  private _rootFolderId: string;
  private _accessToken: string = null;
  private _popupClosedSubject = new Subject<void>(); // Event when popup closes
  popupClosed$ = this._popupClosedSubject.asObservable(); // Observable to listen for popup closed event
  private _projectListSubject = new BehaviorSubject<Bim360Object[]>([]); // Event when popup closes
  bimProjectList$ = this._projectListSubject.asObservable(); // Observable to listen for popup closed event
  private issuesTypes: { id: string; title: string };

  constructor(
    private endPoint: RestfulEndpoint,
    private _http: HttpClient,
    private _toaster: ToastrService,
    private _translocoService: TranslocoService,
    public _project: Project360Service,
  ) {
    this._accessToken = localStorage.getItem('bim360AccessToken');
  }

  setAuthorizationCode(token: string) {
    this.authorizationCode = token;
    localStorage.setItem('bim360Token', token);
  }

  setAccessToken(token: string) {
    this._accessToken = token;
    localStorage.setItem('bim360AccessToken', token);
  }

  resetAccessToken() {
    this.setAccessToken(null);
  }

  getAccessToken() {
    return localStorage.getItem('bim360AccessToken');
  }

  setBim360HubId(hubId: string) {
    this._hubId = hubId;
  }

  getBim360HubId() {
    return this._hubId;
  }

  setBim360ProjectId(id) {
    this._projectId = id;
  }

  setBim360ProjectName(name) {
    this.project_name = name;
  }

  getBim360ProjectName() {
    return this.project_name;
  }

  getBim360ProjectId() {
    return this._projectId;
  }

  setBim360RootFolderId(id) {
    this._rootFolderId = id;
  }

  saveAutodeskAccount(body: {
    project_id: string;
    token: string;
    refresh_token?: string;
    autodesk_project_id?: string;
    autodesk_hub_id?: string;
    autodesk_container_id?: string;
    autodesk_root_folder_id?: string;
    autodesk_project_name?: string;
    token_expiry_date?: any;
  }): Observable<any> {
    return this.endPoint.post('save_autodesk_account', body);
  }

  getBim360RootFolderId() {
    return this._rootFolderId;
  }

  fetchBimFileFromBim360(
    project_id: string,
    url: string,
    autodesk_file_id: any,
    autodesk_file_name: string,
    autodesk_file_version: string,
    autodesk_file_last_modified_time: string,
  ): Observable<any> {
    return this.endPoint.post('fetch_bimfile_from_autodesk_bim360', {
      project_id: project_id,
      url: url,
      autodesk_file_id: autodesk_file_id,
      autodesk_file_name: autodesk_file_name,
      autodesk_file_version: autodesk_file_version,
      autodesk_file_last_modified_time: autodesk_file_last_modified_time,
    });
    // return this._httpClient.post('api/auth/forgot-password', email);
  }

  //using this method to get the token from autodesk
  getToken(code: string): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: this.authBaseCode,
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
      skipAuth: 'true',
    });

    const params = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('code', code)
      .set('redirect_uri', this.redirectUri);

    return this._http.post(this.tokenEndpointUrl, params.toString(), { headers }).pipe(
      map((response) => response),
      catchError((error) => {
        console.error('Error exchanging authorization code for access token:', error);
        return null; // Return null in case of error, can be modified as needed
      }),
    );
  }

  openBIMLoginPopup(): any {
    const width = 800;
    const height = 600;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    //opening a popup for autodesk log in page
    const popup = window.open(
      this.authUrl,
      'OAuth Sign-In',
      `width=${width}, height=${height}, left=${left}, top=${top}`,
    );

    //tracking what happens in the popup
    const pollTimer = setInterval(() => {
      if (popup.closed || popup.closed === undefined) {
        clearInterval(pollTimer);
        this._popupClosedSubject.next();
      } else if (popup.location.href.startsWith(this.redirectUri)) {
        // The popup's URL matches the redirect URI
        const callbackUrl = popup.location.href;
        const authorizationCode = new URL(callbackUrl).searchParams.get('code');

        if (authorizationCode) {
          // Getting the access token from autodesk using auth code
          this.getToken(authorizationCode).subscribe(
            (response) => {
              if (response) {
                //storing the access token
                this.setAccessToken(response.access_token);
              }
            },
            (error) => {
              this.resetAccessToken();
              console.error('Error exchanging authorization code for access token:', error);
            },
          );

          // Close the popup
          popup.close();
        }
      }
    }, 1000);
  }

  async getBim360Hubs() {
    return new Promise((resolve, reject) => {
      this.api_getBim360Hubs(this.getAccessToken()).subscribe(
        (res) => {
          if (res?.data?.length > 0) {
            this.setBim360HubId(res.data[0].id);
            this.getBim360Projects();
            this.isLoggedIn = true;
            resolve(true);
          } else {
            this.resetAccessToken();
            // this._toaster.error(
            //   this._translocoService.translate(
            //     'You do not have permission to access ACC HUB, kindly make sure that you have added WhiteHelmet a custom integration in your ACC admin panel',
            //   ),
            // );
            resolve(false);
          }
        },
        (error) => {
          this.resetAccessToken();
          reject(error);
        },
      );
    });
  }

  getBim360ProjectsFolders(
    projectId: string,
    token: string,
    rootFolderId: string,
  ): Observable<any> {
    const projUrl = `${this.ProjectsEndpointUrl}/${projectId}/folders/${rootFolderId}/contents`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      skipAuth: 'true',
    });

    return this._http.get<any>(projUrl, { headers, observe: 'response' }).pipe(
      map((response) => response.body),
      catchError((error) => {
        console.error('Error fetching BIM 360 Project Folders:', error);
        return of(null);
      }),
    );
  }

  getBimFileStorage(projectId: string, token: string, fileId: string): Observable<any> {
    const projUrl = `${this.ProjectsEndpointUrl}/${projectId}/items/${fileId}`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      skipAuth: 'true',
    });

    return this._http.get<any>(projUrl, { headers, observe: 'response' }).pipe(
      map((response) => response.body),
      catchError((error) => {
        console.error('Error fetching BIM file storage:', error);
        return of(null);
      }),
    );
  }

  generateDownloadingUrl(fileStorageUrl: string, token: string): Observable<any> {
    const url = fileStorageUrl.split('?scopes')[0] + '/signeds3download';
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      skipAuth: 'true',
    });

    return this._http.get<any>(url, { headers, observe: 'response' }).pipe(
      map((response) => response.body),
      catchError((error) => {
        console.error('Error generating download URL:', error);
        return of(null);
      }),
    );
  }

  //Using this method to generate the Authentication header
  //for getting token request
  generateBasicAuthHeader(clientId: string, clientSecret: string): string {
    const credentials = `${clientId}:${clientSecret}`;
    const base64Credentials = buffer.Buffer.from(credentials).toString('base64');
    const authHeader = `Basic ${base64Credentials}`;
    return authHeader;
  }

  getAuthorizationCode() {
    return this.authorizationCode;
  }

  getBim360Projects() {
    if (this.getAccessToken() && this.getBim360HubId()) {
      this.api_getBim360Projects(this.getBim360HubId(), this.getAccessToken()).subscribe(
        (projects) => {
          if (projects) {
            this.projects = projects;
            projects.data.forEach((project) => {
              const projectName = project.attributes.name;
              const projectId = project.id;
              const rootFolderId = project.relationships.rootFolder.data.id;

              // Check if the project with the same id already exists in the projectList
              const existingProject = this.bimProjectList.find((p) => p.id === projectId);
              if (!existingProject) {
                this.bimProjectList.push({
                  name: projectName,
                  id: projectId,
                  rootFolderId: rootFolderId,
                });
              }
            });

            this._projectListSubject.next([...this.bimProjectList]);
            //setting project name
            this.setBim360ProjectId(projects.data[0].id);
            //setting rootFolder id
            this.setBim360RootFolderId(projects.data[0].relationships.rootFolder.data.id);
          } else {
            console.error('Failed to obtain projects.');
          }
        },
        (error) => {},
      );
    }
  }

  api_getIssueTypes(bim_project_id: string, token: string): Observable<any> {
    const issueTypesUrl = `${this.apiBaseUrl}/construction/issues/v1/projects/${bim_project_id}/issue-types?include=subtypes`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      skipAuth: 'true',
    });

    return this._http.get<any>(issueTypesUrl, { headers, observe: 'response' }).pipe(
      map((response) => response.body),
      catchError((error) => {
        console.error('Error fetching BIM 360 Projects:', error);
        return of(null);
      }),
    );
  }

  api_postAccIssue(bim_project_id: string, token: string, body: any): Observable<any> {
    const issueTypesUrl = `${this.apiBaseUrl}/construction/issues/v1/projects/${bim_project_id}/issues`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      skipAuth: 'true',
    });

    return this._http.post<any>(issueTypesUrl, body, { headers, observe: 'response' });
  }

  api_getCreateIssuePermission(bim_project_id: string, token: string): Observable<any> {
    const issueTypesUrl = `${this.apiBaseUrl}/construction/issues/v1/projects/${bim_project_id}/users/me`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      skipAuth: 'true',
    });

    return this._http.get<any>(issueTypesUrl, { headers, observe: 'response' }).pipe(
      map((response) => response.body),
      catchError((error) => {
        console.error('Error fetching BIM 360 Projects:', error);
        return of(null);
      }),
    );
  }

  async getBimIssuesTypes(project_id: string): Promise<{ id: string; title: string }[]> {
    return new Promise((resolve, reject) => {
      this.api_getIssueTypes(project_id.split('.')[1], this.getAccessToken()).subscribe(
        (response) => {
          const issue_types = response.results
            .filter((issue) => issue.isActive) //get the active types only
            .flatMap((issue) =>
              issue.subtypes.map((subtype) => ({
                id: subtype.id,
                title: subtype.title,
              })),
            );
          resolve(issue_types);
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  private api_getBim360Hubs(token: string): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      skipAuth: 'true',
    });

    return this._http.get<any>(this.hubsEndpointUrl, { headers, observe: 'response' }).pipe(
      map((response) => response.body),
      catchError((error) => {
        console.error('Error fetching BIM 360 Hubs:', error);
        return of(null);
      }),
    );
  }

  private api_getBim360Projects(hubId: string, token: string): Observable<any> {
    const projUrl = `${this.hubsEndpointUrl}/${hubId}/projects`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      skipAuth: 'true',
    });

    return this._http.get<any>(projUrl, { headers, observe: 'response' }).pipe(
      map((response) => response.body),
      catchError((error) => {
        console.error('Error fetching BIM 360 Projects:', error);
        return of(null);
      }),
    );
  }
}
